import { useMemo } from 'react';

import { useHdrProcessingRateMins } from '@/api/devices';
import {
    PostHistoricalWindDataPayload,
    PostHistoricalWindDataResponse,
    WindDataItemFormattedWithUnits,
    WindHistoryItem,
} from '@/api/historicalData';
import { combineProcessingSources } from '@/api/historicalData/helpers';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { convert } from '@/common/convert';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { HISTORICAL_WIND_DATA_QUERY_KEY } from '@/config';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { DateRange, useGlobalStore } from '@/store';
import { maybeMockNestedHdrResponse } from '@/utils/mockDataHelpers';

export const postHistoricalWindData = async (
    payload: PostHistoricalWindDataPayload,
    options?: RequestOptions
): Promise<WindHistoryItem[]> => {
    const response: PostHistoricalWindDataResponse = await maybeMockNestedHdrResponse(
        axios.post('/fetch/wind-data-history', payload, {
            ...requestConfig(options),
        }),
        'windHistory'
    );

    return response.data.windHistory.map(({ location, ...item }) => ({ ...item, ...location }));
};

type UseHistoricalWindDataOptions = Omit<PostHistoricalWindDataPayload, 'spotterId' | 'startDate' | 'endDate'> & {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    config?: Omit<QueryConfig<typeof postHistoricalWindData>, 'select'>;
};

/**
 * Historical wind data for the range, in the user's preferred units, with embedded samples filling the gaps
 * between the hdr ones.
 */
export const useHistoricalWindData = ({
    spotterId,
    dateRange,
    processingSources,
    config,
}: UseHistoricalWindDataOptions): CombinedQueryResult<WindDataItemFormattedWithUnits[]> => {
    const processingSource = useGlobalStore((state) => state.processingSource);
    const { data: windDataRaw, ...rest } = useLiveSplitQuery({
        queryKeyPrefix: HISTORICAL_WIND_DATA_QUERY_KEY,
        spotterId,
        dateRange,
        keyOptions: { processingSources },
        fetchRange: ({ viewToken, signal, ...range }) =>
            postHistoricalWindData({ ...range, processingSources }, { viewToken, signal }),
        // one of the older Spotter entrypoints, so it excludes the bounds it is given
        apiDateBounds: 'exclusive',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * wavefleet queries a window once per device row and concatenates the results. The sort it finishes
         * with keys on a top level `timestamp`, which these samples carry under `location`, so it does not
         * order them: a spotter whose device was replaced answers in device order, not time order.
         */
        responseSortOrder: 'unsorted',
        config,
    });
    const hdrProcessingRateMins = useHdrProcessingRateMins({
        spotterId,
        config: { enabled: config?.enabled !== false },
    });
    const units = usePreferredUnits('speed');
    const formatted = useMemo<WindDataItemFormattedWithUnits[] | undefined>(
        () =>
            combineProcessingSources(
                windDataRaw?.map((item) => {
                    return {
                        seasurfaceId: item.seasurfaceId,
                        direction: item.direction,
                        timestamp: item.timestamp,
                        speed: convert(item.speed).from('m/s').to(units),
                        processing_source: item.processing_source,
                        windSpeedUnits: units,
                        windSpeedUnitType: 'speed',
                    };
                }),
                hdrProcessingRateMins,
                processingSource
            ),
        [windDataRaw, hdrProcessingRateMins, units, processingSource]
    );

    return { data: formatted, ...rest };
};
